import { ROUTES } from '@dealstack/frontend/investor/pages/Routing/routes';
import { useInvestmentJoinAndAllocationController } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/useInvestmentJoinAndAllocationController';
import { useBackupController } from '@dealstack/frontend/investor/pages/Investment/common/useBackupController';
import { useInvestorDataAdapter } from '@dealstack/frontend/investor/pages/StandaloneKyc/hooks/useInvestorDataAdapter';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useStandaloneKycMetadata } from '@dealstack/frontend/investor/pages/StandaloneKyc/hooks/useStandaloneKycMetadata';
import { useStandaloneKycSteps } from '@dealstack/frontend/investor/pages/StandaloneKyc/hooks/useStandaloneKycSteps';
import { GetInvestmentDetailsRes } from '@dealstack/shared/lib/generated';

type ContextType = ReturnType<typeof useInvestmentJoinAndAllocationController>;

export const useStandaloneKycContext = (
	metadataHook: ReturnType<typeof useStandaloneKycMetadata>,
	stepsHook: ReturnType<typeof useStandaloneKycSteps>
): ContextType => {
	const { customDetailsQuery } = metadataHook;
	const { dataBackup, updateBackupData, removeBackupData } = useBackupController();
	const { kycWorkflowId } = useParams<{ kycWorkflowId: string }>();
	const getInvestorDataRequest = useInvestorDataAdapter(kycWorkflowId);
	const isPIVInvestment = !!getInvestorDataRequest.response?.payload?.pivPerson;

	return useMemo(
		() =>
			({
				kycSetting: metadataHook.kycSetting,
				// TODO(MIP-2520): drop this cast — lift `kycWorkflowId` out of `investmentDetails` so standalone KYC can supply it without faking a `GetInvestmentDetailsRes`.
				investmentDetails: {
					activeKycWorkflowId: kycWorkflowId,
				} as GetInvestmentDetailsRes,
				setIsContinueDisabled: stepsHook.setIsContinueDisabled,
				stepRegistry: stepsHook.stepRegistry,
				confirmsDeclarationsBeforeContracts: false,
				isContinueDisabled: stepsHook.isContinueDisabled,
				isBackDisabled: stepsHook.activeStepKey === 0,
				isLastStep: stepsHook.isLastStep,
				onNextStep: stepsHook.onNextStep,
				onPreviousStep: stepsHook.onPreviousStep,
				onSidebarNavigation: stepsHook.onSidebarNavigation,
				sideBarItems: stepsHook.sideBarItems,
				activeMainStep: stepsHook.activeStep
					? {
							title: stepsHook.activeStep.title,
							route: stepsHook.activeStep.route,
							key: stepsHook.activeStep.key,
							subitems: [],
						}
					: undefined,
				activeSubStep: undefined,
				activeStepKey: stepsHook.activeStepKey,
				activeMainStepArrayIndex: stepsHook.activeStepKey,
				activeSubStepArrayIndex: -1,
				sideBarConfig: [],
				mipId: metadataHook.metadata?.mipId ?? '',
				dealId: metadataHook.metadata?.dealId ?? '',
				investorId: undefined,
				showSaveDraftButton: false,
				showContinueWarning: false,
				isLoading: metadataHook.isLoading,
				isInitialDataReady: !!getInvestorDataRequest.response?.payload,
				setIsLoading: () => undefined,
				setIsBackDisabled: () => undefined,
				nextStep: undefined,
				getInvestorDataRequest,
				getInvestorCustomDetailsRequest: customDetailsQuery,
				isPIVInvestment,
				dataBackup,
				updateBackupData,
				removeBackupData,
				redirectLink: ROUTES.DASHBOARD,
			}) as ContextType,
		[
			kycWorkflowId,
			metadataHook.kycSetting,
			metadataHook.metadata?.mipId,
			metadataHook.metadata?.dealId,
			metadataHook.isLoading,
			stepsHook.isContinueDisabled,
			stepsHook.setIsContinueDisabled,
			stepsHook.activeStepKey,
			stepsHook.isLastStep,
			stepsHook.onNextStep,
			stepsHook.onPreviousStep,
			stepsHook.onSidebarNavigation,
			stepsHook.sideBarItems,
			stepsHook.stepRegistry,
			stepsHook.activeStep,
			customDetailsQuery.data,
			customDetailsQuery.isLoading,
			customDetailsQuery.isError,
			getInvestorDataRequest,
			isPIVInvestment,
			dataBackup,
			updateBackupData,
			removeBackupData,
		]
	);
};
